/**
 * Arm the automatic countdowns on THIS phone (2026-10-30, lib/liveEventPrefs).
 *
 * The Worker starts the iPhone countdown card by push from the account's
 * standing choice, so an iPhone with a push-to-start token needs nothing
 * here. Everything else is armed by the app itself, from the calendar feed
 * it has just loaded, for the next ARM_HORIZON_MS:
 *
 *   - Android, countdown card:   the native LiveActivities plugin's alarms
 *                                (offline, app swiped away, like a tapped
 *                                reminder);
 *   - "a notification only":     a local notification at start minus the
 *                                lead, on either phone;
 *   - an iPhone with no token:   the same local notification, so an older
 *                                iPhone is not left out.
 *
 * The honest limit: a phone arms only when the app opens and loads the feed.
 * A trainer who does not open the app for two weeks gets no new cards on
 * Android; a follow-up can wake the app with a silent push. What has been
 * armed is remembered per event with the lead and delivery it was armed
 * with, so a changed choice re-arms and an unchanged one costs nothing.
 */
import type { GameEvent } from "./data";
import { isAttending } from "./eventAttending";
import { eventsLiveKey } from "./eventsLiveKey";
import { eventReminderAt, notificationIdForEvent } from "./eventReminders";
import { hasLiveActivityDevice, liveActivityReminderAvailable, scheduleLiveActivityReminder } from "./liveActivities";
import { coversEvent, getLiveEventPrefs, leadLabel, type LiveEventPrefs } from "./liveEventPrefs";
import { getPlatform } from "./platform";
import { requestNotificationPermission, scheduleLocalNotificationAt } from "./notifications";

/** How far ahead the phone arms: the feed rarely knows more, and alarms are finite. */
export const ARM_HORIZON_MS = 14 * 24 * 60 * 60_000;

export type ArmableEvent = Pick<GameEvent, "id" | "title" | "start" | "end" | "category" | "spawns" | "bonuses" | "geoLocked">;

/** What an event was armed with; a different signature means arm again. */
export function armSignature(prefs: LiveEventPrefs): string {
  return `${prefs.leadMinutes}:${prefs.delivery}`;
}

const ARMED_KEY = "nexus.liveEventArmed.v1";
let armedCache: Record<string, string> | null = null;

function readArmed(): Record<string, string> {
  if (armedCache) return armedCache;
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(ARMED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    armedCache = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
  } catch {
    armedCache = {};
  }
  return armedCache;
}

function writeArmed(next: Record<string, string>): void {
  armedCache = next;
  try {
    localStorage.setItem(ARMED_KEY, JSON.stringify(next));
  } catch {
    /* private mode: the session still knows */
  }
}

/**
 * Pure: which of these events this phone should arm now. Upcoming within the
 * horizon, covered by the choice (type ticked; a one-place event only when
 * attending), and not already armed with this same lead and delivery.
 */
export function pickEventsToArm(
  events: readonly ArmableEvent[],
  prefs: LiveEventPrefs | null,
  opts: { now?: number; attending?: (eventId: string) => boolean; armed?: Record<string, string> } = {},
): ArmableEvent[] {
  if (!prefs || !prefs.enabled) return [];
  const now = opts.now ?? Date.now();
  const attending = opts.attending ?? (() => false);
  const armed = opts.armed ?? {};
  const signature = armSignature(prefs);
  const out: ArmableEvent[] = [];
  const seen = new Set<string>();
  for (const ev of events) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    if (!coversEvent(prefs, ev, attending(ev.id))) continue;
    const at = eventReminderAt(ev.start, now);
    if (!at) continue;
    if (at.getTime() - now > ARM_HORIZON_MS) continue;
    if (armed[ev.id] === signature) continue;
    out.push(ev);
  }
  return out;
}

export interface ArmOutcome {
  armed: number;
  /** Events the phone could not arm (no permission, a plugin refusal). */
  failed: number;
  /** Nothing to do on this phone: the Worker's push covers it, or the web. */
  skipped: boolean;
}

/** The plain notification, when the card is not wanted or not possible here. */
async function armNotification(ev: ArmableEvent, prefs: LiveEventPrefs, now: number): Promise<boolean> {
  const start = eventReminderAt(ev.start, now);
  if (!start) return false;
  const at = new Date(start.getTime() - prefs.leadMinutes * 60_000);
  if (at.getTime() <= now) return false;
  const lead = leadLabel(prefs.leadMinutes);
  const r = await scheduleLocalNotificationAt({
    id: notificationIdForEvent(ev.id),
    title: lead ? `${ev.title} starts in ${lead}` : `${ev.title} is live`,
    body: lead ? `${ev.title} starts in ${lead} in Pokémon GO.` : `${ev.title} just started in Pokémon GO.`,
    at,
  });
  return r.ok;
}

/**
 * Arm every event the choice covers. Asks for the notification permission
 * once, only when there is something to arm and the phone needs it.
 */
export async function armSubscribedEvents(
  events: readonly ArmableEvent[],
  opts: { prefs?: LiveEventPrefs | null; now?: number } = {},
): Promise<ArmOutcome> {
  const prefs = opts.prefs === undefined ? getLiveEventPrefs() : opts.prefs;
  const now = opts.now ?? Date.now();
  const platform = getPlatform();
  if (platform === "web" || !prefs || !prefs.enabled) return { armed: 0, failed: 0, skipped: true };
  const armed = readArmed();
  const due = pickEventsToArm(events, prefs, { now, attending: isAttending, armed });
  // Forget events that are over, so the record does not grow forever.
  const keep: Record<string, string> = {};
  const live = new Set(events.filter((e) => eventReminderAt(e.start, now) || (e.end && new Date(e.end).getTime() > now)).map((e) => e.id));
  for (const [id, sig] of Object.entries(armed)) if (live.has(id)) keep[id] = sig;
  if (due.length === 0) {
    if (Object.keys(keep).length !== Object.keys(armed).length) writeArmed(keep);
    return { armed: 0, failed: 0, skipped: false };
  }
  const leadMs = prefs.leadMinutes * 60_000;
  // The iPhone card comes from the Worker's push once a token is on file:
  // only the local notification path, or the card for an event already
  // inside the lead, is this phone's to do.
  const workerCovers = platform === "ios" && prefs.delivery === "live" && hasLiveActivityDevice();
  const signature = armSignature(prefs);
  let count = 0;
  let failed = 0;
  let permission: string | null = null;
  for (const ev of due) {
    let ok = false;
    try {
      if (prefs.delivery === "live") {
        const start = eventReminderAt(ev.start, now);
        const canCard = await liveActivityReminderAvailable(start?.getTime(), now, leadMs);
        if (canCard && (!workerCovers || (start && start.getTime() - now <= leadMs))) {
          const r = await scheduleLiveActivityReminder(ev, now, { leadMs, accountCovered: true });
          ok = r.ok;
        } else if (workerCovers) {
          ok = true; // the push does it
        } else {
          if (permission == null) permission = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
          ok = permission === "granted" && (await armNotification(ev, prefs, now));
        }
      } else {
        if (permission == null) permission = await requestNotificationPermission({ reprompt: true }).catch(() => "unknown");
        ok = permission === "granted" && (await armNotification(ev, prefs, now));
      }
    } catch {
      ok = false;
    }
    if (ok) {
      keep[ev.id] = signature;
      count += 1;
    } else {
      failed += 1;
    }
  }
  writeArmed(keep);
  return { armed: count, failed, skipped: false };
}

/** The feed rows the arming pass needs, as /api/events/live serves them. */
interface FeedEventRow {
  id: string;
  name?: string;
  rawType?: string;
  start: string;
  end?: string;
  category?: string;
  spawns?: GameEvent["spawns"];
  bonuses?: string[];
  geoLocked?: boolean;
}

/** Map the live feed's rows onto what the arming pass reads. */
export function feedToArmable(rows: readonly FeedEventRow[]): ArmableEvent[] {
  return rows.map((r) => ({
    id: r.id,
    title: r.name ?? r.rawType ?? "Event",
    start: r.start,
    end: r.end,
    category: (r.category as GameEvent["category"]) ?? "event",
    spawns: r.spawns,
    bonuses: r.bonuses,
    ...(r.geoLocked ? { geoLocked: true } : {}),
  }));
}

/**
 * Arm from the live feed, fetching it through the app's query client (the
 * Calendar's own cache when it is warm). For the sheet's "Turn on" tap, a
 * Settings change and the launch pass; best-effort, never throws.
 */
export async function armSubscribedEventsFromFeed(
  client: { fetchQuery: (opts: { queryKey: readonly unknown[]; staleTime?: number }) => Promise<unknown> },
  opts: { prefs?: LiveEventPrefs | null; now?: number } = {},
): Promise<ArmOutcome> {
  const prefs = opts.prefs === undefined ? getLiveEventPrefs() : opts.prefs;
  if (getPlatform() === "web" || !prefs || !prefs.enabled) return { armed: 0, failed: 0, skipped: true };
  try {
    const data = (await client.fetchQuery({ queryKey: [eventsLiveKey()], staleTime: 5 * 60_000 })) as
      | { active?: FeedEventRow[]; upcoming?: FeedEventRow[] }
      | undefined;
    const rows = [...(data?.active ?? []), ...(data?.upcoming ?? [])];
    return await armSubscribedEvents(feedToArmable(rows), { prefs, now: opts.now });
  } catch (err) {
    console.warn("[liveEventArming] feed arm failed", err);
    return { armed: 0, failed: 0, skipped: true };
  }
}

/** Tests only. */
export function __resetLiveEventArmingForTest(): void {
  armedCache = null;
  try {
    localStorage.removeItem(ARMED_KEY);
  } catch {
    /* ignore */
  }
}
